import React, { useState, useEffect } from 'react';
import AppImage from '../../../components/AppImage';
import WaitlistSignup from './WaitlistSignup';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleWaitlistSuccess = (userData) => {
    console.log('Waitlist signup successful:', userData);
  };

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center" style={{background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3a5c 40%, #0d2d4a 70%, #0a1628 100%)'}}>
      {/* Animated background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full opacity-20 animate-breathing" style={{background: 'radial-gradient(circle, #2dd4bf 0%, transparent 70%)'}}></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full opacity-15 animate-breathing" style={{background: 'radial-gradient(circle, #ff6b6b 0%, transparent 70%)', animationDelay: '2s'}}></div>
        <div className="absolute top-[30%] right-[20%] w-[300px] h-[300px] rounded-full opacity-10 animate-breathing" style={{background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)', animationDelay: '4s'}}></div>
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px'}}></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        
        {/* Logo — centered across full width */}
        <div className={`flex justify-center mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <img
            src="/assets/images/73F93F14-F2E8-4E4E-A466-5C4C42E82EEC-1774045706328.png"
            alt="Cynizee - Organizing Chaos logo"
            style={{ width: '480px', maxWidth: '90%', mixBlendMode: 'screen', filter: 'brightness(1.1) contrast(1.05)' }}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left: Content */}
          <div className={`transition-all duration-1000 text-center ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            
            {/* Launch badge */}
            <div className="mb-6">
              {/* iOS-only notice */}
              <div className="flex flex-wrap justify-center items-center gap-3 mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border" style={{background: 'rgba(45,212,191,0.12)', borderColor: 'rgba(45,212,191,0.4)', color: '#2dd4bf'}}>
                  <Icon name="Smartphone" size={15} />
                  iOS Only at Launch
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border" style={{background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.55)'}}>
                  <Icon name="Clock" size={14} />
                  Google Play Store — Coming Soon
                </div>
              </div>
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border" style={{background: 'rgba(255,107,107,0.15)', borderColor: 'rgba(255,107,107,0.4)', color: '#ff8a8a'}}>
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{background: '#ff6b6b'}}></span>
                  Coming Soon — Join the Waitlist
                </span>
              </div>
            </div>

            {/* Main headline */}
            <h1 className="font-heading font-bold leading-[1.1] mb-6 text-white" style={{fontSize: 'clamp(2.5rem, 5vw, 4rem)'}}>
              Less Nagging.{' '}
              <span style={{color: '#ff6b6b'}}>More Accountability.</span>
              <br />
              <span style={{background: 'linear-gradient(90deg, #2dd4bf, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>Better Habits.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg leading-relaxed mb-10 mx-auto" style={{color: 'rgba(255,255,255,0.7)', maxWidth: '520px'}}>
              Cynizee helps families manage chores, build responsibility, and create real follow-through — with reminders, approvals, proof of completion, and smart accountability tools.
            </p>

            {/* Key stats row */}
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              {[
                { icon: 'Lock', label: 'App Locking', color: '#ff6b6b' },
                { icon: 'CheckCircle', label: 'Chore Tracking', color: '#2dd4bf' },
                { icon: 'Camera', label: 'Proof Submission', color: '#f59e0b' },
              ]?.map((stat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: `${stat?.color}20`}}>
                    <Icon name={stat?.icon} size={16} style={{color: stat?.color}} />
                  </div>
                  <span className="text-sm font-medium" style={{color: 'rgba(255,255,255,0.8)'}}>{stat?.label}</span>
                </div>
              ))}
            </div>

            {/* Waitlist form */}
            <div data-waitlist-section className="rounded-2xl p-6 border" style={{background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)'}}>
              <h2 className="text-lg font-heading font-bold text-white mb-1">Get Early Access</h2>
              <p className="text-sm mb-5" style={{color: 'rgba(255,255,255,0.5)'}}>Be the first family to experience Cynizee.</p>
              <WaitlistSignup onSuccess={handleWaitlistSuccess} />
            </div>
          </div>

          {/* Right: Phone mockup + floating cards */}
          <div className={`relative flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            
            {/* Floating feature cards */}
            <div className="absolute -left-4 top-[10%] z-20 rounded-xl px-4 py-3 shadow-xl border" style={{background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', animation: 'fade-in-up 1s ease-out 0.8s both'}}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: '#ff6b6b'}}>
                  <Icon name="Lock" size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">Apps Locked</p>
                  <p className="text-xs" style={{color: 'rgba(255,255,255,0.6)'}}>Chores not done yet</p>
                </div>
              </div>
            </div>

            <div className="absolute -right-2 top-[40%] z-20 rounded-xl px-4 py-3 shadow-xl border" style={{background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', animation: 'fade-in-up 1s ease-out 1.1s both'}}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: '#2dd4bf'}}>
                  <Icon name="CheckCircle" size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">Chore Approved!</p>
                  <p className="text-xs" style={{color: 'rgba(255,255,255,0.6)'}}>Great job, Emma 🎉</p>
                </div>
              </div>
            </div>

            <div className="absolute -left-2 bottom-[15%] z-20 rounded-xl px-4 py-3 shadow-xl border" style={{background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', animation: 'fade-in-up 1s ease-out 1.4s both'}}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: '#f59e0b'}}>
                  <Icon name="Camera" size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">Proof Submitted</p>
                  <p className="text-xs" style={{color: 'rgba(255,255,255,0.6)'}}>Awaiting review</p>
                </div>
              </div>
            </div>

            {/* Phone frame */}
            <div className="relative w-56 sm:w-64 lg:w-72">
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-[2.5rem] blur-2xl opacity-60 scale-110" style={{background: 'linear-gradient(135deg, #2dd4bf, #ff6b6b, #f59e0b)'}}></div>
              {/* Phone border */}
              <div className="relative rounded-[2.5rem] p-[3px] shadow-2xl" style={{background: 'linear-gradient(135deg, #2dd4bf, #ff6b6b, #f59e0b)'}}>
                <div className="bg-black rounded-[2.3rem] overflow-hidden">
                  {/* Notch */}
                  <div className="flex justify-center pt-3 pb-1">
                    <div className="w-20 h-5 bg-black rounded-full border border-gray-800"></div>
                  </div>
                  <AppImage
                    src="/assets/images/IMG_6593-1774042423911.jpeg"
                    alt="Cynizee app splash screen showing three hexagon logos, CYNIZÉE branding, and Organizing Chaos tagline on gradient teal-to-orange background"
                    className="w-full h-auto object-cover"
                  />
                  {/* Home indicator */}
                  <div className="flex justify-center py-3">
                    <div className="w-24 h-1 bg-gray-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom scroll hint */}
        <div className={`flex justify-center mt-16 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs uppercase tracking-widest" style={{color: 'rgba(255,255,255,0.3)'}}>Scroll to explore</span>
            <div className="w-5 h-8 border-2 rounded-full flex justify-center pt-1.5" style={{borderColor: 'rgba(255,255,255,0.2)'}}>
              <div className="w-1 h-2 rounded-full animate-bounce" style={{background: 'rgba(255,255,255,0.4)'}}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;